import { Autocomplete, Box, Button, Grid, TextField, Typography } from '@mui/material';

import { SubmitHandler, useForm } from 'react-hook-form';

import { useTranslation } from '../../contexts/translation';

import { useEffect, useState } from 'react';

import { SelectBox } from '../../components/core/SelectBox/SelectBox';

import { useMutation, useQuery } from 'react-query';

import * as apiDashboard from '../../api/app/dashboardsApi';

import * as api from '../../api/app/dashboardsOpcButtonApi';

import * as apiOpcClient from '../../api/app/opcClientApi';

import {
  dashboardOpcButtonDataTypes,
  DashboardOpcButtonInputs,
  dashboardOpcButtonStyles,
  DashboardOpcButtonTypes,
  dashboardOpcButtonTypes,
} from '../../types/app/dashboardOpcButton';

import { useSnackbar } from 'notistack';

import { AlertDialog } from '../../components/core/AlertDialog';

import { useDeleteDialog } from '../../hooks/useDeleteDialog';

import { Dashboard, DashboardTypes } from '../../types/app/dashboards.types';

import { Loader } from '../../components/core/Loader/Loader';

import { SelectBoxAutocomplete } from '../../components/core/SelectBox/SelectBoxAutocomplete';

import { OpcClient } from '../../types/app/opcClient.types';

import { dictionary } from '../../utils/core/dictionary';

interface Props {
  defaultValues: DashboardOpcButtonInputs;
  isEdit: boolean;
  assetId: number;
  onSuccess: () => void;
}

export const DashboardOpcButtonForm = ({ defaultValues, isEdit, assetId, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isOpen: isDeleteDialogOpen, setDeleteAttempt, cancelDeleteAttempt } = useDeleteDialog();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<DashboardOpcButtonInputs>();

  const [selectedType, setSelectedType] = useState<DashboardOpcButtonTypes | null>(
    isEdit ? (defaultValues.type as DashboardOpcButtonTypes) : null,
  );
  const [endpointAddress, setEndpointAddress] = useState<string>('');
  const [nodeList, setNodeList] = useState<string[]>();

  const { mutate: mutateCreateOpcButton } = useMutation(api.createOpcButton);
  const { mutate: mutateUpdateOpcButton } = useMutation(api.updateOpcButton);
  const { mutate: mutateDeleteOpcButton } = useMutation(api.deleteOpcButton);

  const { data: dashboardList } = useQuery<Dashboard[]>(['dashboards', assetId], () =>
    apiDashboard.getDashboardList(assetId),
  );
  const { data: opcClientList } = useQuery<OpcClient[]>(
    'opcClientList',
    apiOpcClient.getOpcClientList,
  );

  useQuery<string[]>(['nodeList', endpointAddress], () => apiOpcClient.browse(endpointAddress), {
    enabled: endpointAddress.length > 0,
    onSuccess: (res) => {
      let tmpNodeList: string[] = [];
      res.map((r: string) => {
        tmpNodeList = [...tmpNodeList, r];
      });
      setNodeList(tmpNodeList);
    },
  });

  useEffect(() => {
    if (!defaultValues?.opcClientId && opcClientList) return;
    let tmpC = opcClientList?.find((x: any) => x.id === defaultValues?.opcClientId);
    if (tmpC) setEndpointAddress(tmpC.endpointAddress);
  }, [defaultValues && defaultValues?.opcClientId]);

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  const onSubmit: SubmitHandler<DashboardOpcButtonInputs> = (data: DashboardOpcButtonInputs) => {
    const requestData = {
      ...data,
      row: Number(data.row),
      column: Number(data.column),
      type: selectedType,
    };

    if (isEdit) {
      // Update
      mutateUpdateOpcButton(
        { ...requestData },
        {
          onSuccess: () => {
            enqueueSnackbar(dictionary.OPC_BUTTON_EDITED, { variant: 'success' });
            onSuccess();
          },
          onError: (result) => {
            enqueueSnackbar(`${result}`, { variant: 'error' });
          },
        },
      );
    } else {
      // Creaate
      mutateCreateOpcButton(
        { ...requestData },
        {
          onSuccess: () => {
            enqueueSnackbar(dictionary.OPC_BUTTON_ADDED, { variant: 'success' });
            onSuccess();
          },
          onError: (result) => {
            enqueueSnackbar(`${result}`, { variant: 'error' });
          },
        },
      );
    }
  };

  const handleConfirmDelete = () => {
    mutateDeleteOpcButton(defaultValues.id!, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.OPC_BUTTON_DELETED, { variant: 'success' });
        onSuccess();
      },
      onError: (result) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (!dashboardList || !opcClientList) return <Loader />;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        border: '1px solid #000',
        maxWidth: '90%',
        boxShadow: 24,
        overFlow: 'auto',
        pt: 2,
        px: 0,
        pb: 3,
      }}
    >
      <Typography id="modal-modal-title" variant="h6" component="h2" px={4} mb={2}>
        {isEdit ? <>Edit button</> : <>Add button</>}
      </Typography>
      <div style={{ padding: 10 }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <SelectBox
                label="Type"
                items={dashboardOpcButtonTypes}
                valueKey={'code'}
                labelKey={'name'}
                onChange={(event: any) => setSelectedType(event)}
                controllerProps={{
                  name: 'type',
                  control: control,
                  rules: { required: `Type ${dictionary.IS_REQUIRED}` },
                  defaultValue: defaultValues?.type,
                }}
                isRequired
                errors={errors}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Name"
                {...register('name', { required: `Name ${dictionary.IS_REQUIRED}` })}
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <SelectBox
                label="Style"
                items={dashboardOpcButtonStyles}
                valueKey={'code'}
                labelKey={'name'}
                controllerProps={{
                  name: 'style',
                  control: control,
                  rules: { required: `Style ${dictionary.IS_REQUIRED}` },
                  defaultValue: defaultValues?.type,
                }}
                isRequired
                errors={errors}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                InputProps={{ inputProps: { min: 0.0, step: '0.1' } }}
                label="Font size"
                {...register('fontSize', {
                  required: `Font size ${dictionary.IS_REQUIRED}`,
                })}
                error={Boolean(errors.fontSize)}
                helperText={errors.fontSize?.message}
                defaultValue={15}
              />
            </Grid>
            <div
              style={{
                height: 1,
                width: '100%',
                // backgroundColor: '#F8F8F8',
                marginTop: 10,
                marginBottom: 10,
              }}
            ></div>

            <Grid item xs={12}>
              <SelectBoxAutocomplete
                label={'Link to dashboard'}
                items={dashboardList.filter(
                  (x) =>
                    x.type === DashboardTypes.OPC_DASHBOARD && x.id !== defaultValues.dashboardId,
                )}
                valueKey={'id'}
                labelKey={'name'}
                controllerProps={{
                  name: 'linkToDashBoard',
                  control: control,
                  rules:
                    selectedType === DashboardOpcButtonTypes.LINK_TO_DASHBOARD
                      ? { required: `Link to dashboard ${dictionary.IS_REQUIRED}` }
                      : {},
                  defaultValue: defaultValues?.linkToDashBoard,
                }}
                errors={errors}
                isRequired={selectedType === DashboardOpcButtonTypes.LINK_TO_DASHBOARD}
              />
            </Grid>

            {selectedType &&
              (selectedType === DashboardOpcButtonTypes.COUNTER_INCREMENT ||
                selectedType === DashboardOpcButtonTypes.SET_NEW_VALUE ||
                selectedType === DashboardOpcButtonTypes.SET_TRUE ||
                selectedType === DashboardOpcButtonTypes.SET_FALSE ||
                selectedType === DashboardOpcButtonTypes.SET_VALUE_TO_ZERO) && (
                <Grid item xs={12}>
                  <SelectBoxAutocomplete
                    isRequired
                    label={dictionary.OPCCLIENT}
                    items={opcClientList}
                    valueKey={'id'}
                    labelKey={'instanceName'}
                    onChange={(e: any) => {
                      let tmpC = opcClientList.find((x: OpcClient) => x.id === e);
                      if (tmpC) setEndpointAddress(tmpC?.endpointAddress);
                    }}
                    controllerProps={{
                      name: 'opcClientId',
                      control: control,
                      rules: { required: 'OPC client ' + dictionary.IS_REQUIRED },
                    }}
                    errors={errors}
                  />
                </Grid>
              )}

            {selectedType &&
              (selectedType === DashboardOpcButtonTypes.COUNTER_INCREMENT ||
                selectedType === DashboardOpcButtonTypes.SET_NEW_VALUE ||
                selectedType === DashboardOpcButtonTypes.SET_TRUE ||
                selectedType === DashboardOpcButtonTypes.SET_FALSE ||
                selectedType === DashboardOpcButtonTypes.SET_VALUE_TO_ZERO) && (
                <Grid item xs={12}>
                  {!nodeList && (
                    <TextField
                      fullWidth
                      required
                      label="Node ID"
                      {...register('nodeId', { required: `Node id ${dictionary.IS_REQUIRED}` })}
                    />
                  )}
                  {nodeList && (
                    <Autocomplete
                      id="free-solo-demo"
                      freeSolo
                      value={
                        defaultValues && defaultValues.nodeId ? defaultValues.nodeId : 'ns=2;s='
                      }
                      options={nodeList.map((node) => node)}
                      onInputChange={(e, data) => setValue('nodeId', data)}
                      renderInput={(params) => <TextField {...params} label="Node ID" />}
                    />
                  )}
                </Grid>
              )}

            {selectedType && selectedType === DashboardOpcButtonTypes.COUNTER_INCREMENT && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  InputProps={{ inputProps: { min: 0.0, step: '0.1' } }}
                  label="Counter increment difference"
                  {...register('counterIncrementDifference', {
                    required: `Counter increment difference ${dictionary.IS_REQUIRED}`,
                  })}
                  error={Boolean(errors.counterIncrementDifference)}
                  helperText={errors.counterIncrementDifference?.message}
                />
              </Grid>
            )}

            {selectedType &&
              (selectedType === DashboardOpcButtonTypes.COUNTER_INCREMENT ||
                selectedType === DashboardOpcButtonTypes.SET_NEW_VALUE ||
                selectedType === DashboardOpcButtonTypes.SET_TRUE ||
                selectedType === DashboardOpcButtonTypes.SET_FALSE ||
                selectedType === DashboardOpcButtonTypes.SET_VALUE_TO_ZERO) && (
                <Grid item xs={12}>
                  <SelectBox
                    label="Data type"
                    items={dashboardOpcButtonDataTypes}
                    valueKey={'code'}
                    labelKey={'name'}
                    controllerProps={{
                      name: 'opcValueDataType',
                      control: control,
                      rules: { required: `Data type ${dictionary.IS_REQUIRED}` },
                      defaultValue: defaultValues?.opcValueDataType,
                    }}
                    isRequired
                    errors={errors}
                  />
                </Grid>
              )}

            {selectedType &&
              (selectedType === DashboardOpcButtonTypes.SET_TRUE ||
                selectedType === DashboardOpcButtonTypes.SET_FALSE) && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    InputProps={{ inputProps: { min: 0, step: '1', pattern: '\\d+' } }}
                    label="Pulse in ms"
                    {...register('pulseInMs', {
                      validate: (value) => Number.isInteger(Number(value)) || 'Enter an integer',
                    })}
                    error={Boolean(errors.pulseInMs)}
                    helperText={errors.pulseInMs?.message}
                  />
                </Grid>
              )}

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Button type="submit" variant="contained" color="success">
                  {t('general.action-buttons.save')}
                </Button>
                {isEdit && defaultValues && (
                  <Button
                    type="button"
                    variant="contained"
                    color="error"
                    onClick={() => setDeleteAttempt(defaultValues.id!)}
                  >
                    {t('general.action-buttons.delete')}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </div>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirmDelete}
      />
    </Box>
  );
};
